import moment from "moment-timezone";
import {
  Consent,
  ContractorType,
  FormikContractorLegal,
  FormikContractorNatural,
  FormikInsurance,
  FormikInsured,
  Fractionation,
  Insured,
  LegalContractor,
  NaturalContractor,
  Policy,
} from "src/types";
import { deleteEmptyKeys } from "src/utils";

const toConsensus = (values: FormikInsurance): Consent[] => {
  const consensus: Consent[] = [];

  if (values.informativeVision) {
    consensus.push({
      cod_consenso: "C_01",
      nome: "informativa_precontrattuale",
      descrizione:
        "Varie informazioni relative al consenso informativa precontrattuale",
    });
  }

  if (values.consentTd) {
    consensus.push({
      cod_consenso: "C_02",
      nome: "tramissione_digitale",
      descrizione: "Consenso alla trasmissione digitale",
    });
  }

  if (values.consentPrivacy) {
    consensus.push({
      cod_consenso: "C_03",
      nome: "privacy",
      descrizione: "Consenso alla privacy",
    });
  }

  if (values.consentMarketing) {
    consensus.push({
      cod_consenso: "C_04",
      nome: "marketing",
      descrizione: "Consenso per fini di marketing",
    });
  }

  if (values.consentProfiling) {
    consensus.push({
      cod_consenso: "C_05",
      nome: "profilazione",
      descrizione: "Consenso per la profilazione",
    });
  }

  if (values.consentCommunication) {
    consensus.push({
      cod_consenso: "C_06",
      nome: "cessione_dati",
      descrizione: "Consenso alla cessione dei dati",
    });
  }

  return consensus;
};

const toNaturalContractor = (
  values: FormikContractorNatural
): NaturalContractor | undefined => {
  try {
    return deleteEmptyKeys({
      nome: values.contractorName,
      cognome: values.contractorSurname,
      sesso: values.contractorGender,
      email: values.contractorEmail,
      telefono: values.contractorPhone.value ?? "",
      nazioneNascita: values.contractorBirthNation,
      luogoNascita: values.contractorBirthPlace,
      provinciaNascita: values.contractorBirthProvince,
      nazionalita: values.contractorNationality,
      cap: values.contractorCap.value,
      dataNascita: moment
        .tz(values.contractorBirth, "DD/MM/YYYY", "Europe/Rome")
        .unix(),
      codiceFiscale: values.contractorFc,
      indirizzo: values.contractorRedisenceAddress,
      comune: values.contractorResidenceMunicipality.value,
      provincia: values.contractorResidenceProvince.value,
    });
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

const toLegalContractor = (
  values: FormikContractorLegal
): LegalContractor | undefined => {
  try {
    return deleteEmptyKeys({
      cognome: values.companyName,
      partitaIva: values.companyVAT,
      email: values.companyEmail,
      telefono: values.companyPhone.value || "",
      codiceFiscale: values.companyFC,
      cap: values.companyLegalCap.value,
      indirizzo: values.companyLegalAddress,
      comune: values.companyLegalCity.value,
      provincia: values.companyLegalProvince.value,
      fcNotMatchVat: values.companyFCNotMatchVAT,
      tipo: values.companyType === "" ? undefined : values.companyType,
    });
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

const toContractor = (
  contractorValues: FormikContractorNatural | FormikContractorLegal,
  contractorType: ContractorType
): NaturalContractor | LegalContractor | undefined => {
  const contractor =
    contractorType === ContractorType.NATURAL
      ? toNaturalContractor(contractorValues as FormikContractorNatural)
      : toLegalContractor(contractorValues as FormikContractorLegal);

  return contractor;
};

const toInsuredList = (values: FormikInsured[]): Insured[] | undefined => {
  try {
    return values.map((insured): Insured => {
      return deleteEmptyKeys({
        nome: insured.insuredName,
        cognome: insured.insuredSurname,
        dataNascita: moment
          .tz(insured.insuredBirth, "DD/MM/YYYY", "Europe/Rome")
          .unix(),
        nazioneNascita: insured.insuredBirthNation,
        luogoNascita: insured.insuredBirthPlace,
        codiceFiscale: insured.insuredFc,
        sesso: insured.insuredGender,
        nazionalita: insured.insuredNationality,
      });
    });
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

export const toPolicyRequest = (
  values: FormikInsurance,
  fractionations: Fractionation[]
): Policy | undefined => {
  const contractor = toContractor(values.contractor, values.contractorType);
  const insuredList = toInsuredList(values.insuredList);
  if (
    !fractionations ||
    fractionations.length === 0 ||
    !contractor ||
    !insuredList ||
    insuredList.length === 0
  )
    return undefined;

  const selectedFractionations = [
    {
      ...fractionations.filter(
        (fractionation) => fractionation.id == values.insuredPeriodType
      )[0],
      selected: true,
    },
  ];

  try {
    return deleteEmptyKeys({
      codProdotto: values.coverageTypeRadios,
      dataInizio: moment
        .tz(values.coverageStartDate, "DD/MM/YYYY", "Europe/Rome")
        .unix(),
      dataFine: moment
        .tz(values.coverageEndDate, "DD/MM/YYYY", "Europe/Rome")
        .set({ hour: 23, minute: 59, second: 59 })
        .unix(),
      durata: Number(values.insuredDaysNumber),
      frazionamenti: selectedFractionations,
      premioTotale: 0,
      consensi: toConsensus(values),
      tipoContraente: values.contractorType,
      contraente: contractor,
      assicurati: insuredList,
      garanzie: [],
      entita: [],
    });
  } catch (e) {
    console.log(e);
    return undefined;
  }
};
